import React from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { puedeVerRuta, primeraRutaPermitida } from "../helpers/permisosPanel";
import { negocioMuestraRuta } from "../helpers/tarjetasDelNegocio";
import OrganizerDashboard from "../organizer/Dashboard";

/*
  Guardas de acceso del panel de organizador, por ruta (organizer-permission-guards, slice 3).

  Viven en su propio archivo, aparte de routes.js, a propósito: routes.js importa todas las
  páginas del sitio (incluido home, que trae swiper), así que un test que solo quiere ejercitar
  estas dos guardas no puede importar routes.js sin arrastrar ese árbol entero. routes.js sigue
  siendo el dueño del árbol de rutas; esto es solo la lógica de guarda, aislada para poder
  testearla.
*/

/** Sin ninguna sección para mandarlo (p. ej. un negocio que no usa Tickets): aviso en vez de loop. */
const SinSecciones = () => (
  <div role="alert" style={{ padding: "2rem", textAlign: "center" }}>
    Este negocio no tiene secciones de Tickets habilitadas para vos.
  </div>
);

/** A la primera sección que el rol y las tarjetas permiten; si no hay (o es esta misma), el aviso. */
const aSeccionPermitida = (auth, ruta) => {
  const destino = primeraRutaPermitida(auth);
  if (!destino || destino === ruta) return <SinSecciones />;
  return <Navigate to={destino} replace />;
};

/**
 * Antes de esta ruta va la de rol (`role !== "ORGANIZER"`, sin cambios). Si el rol pasa, un
 * empleado (`tipo: "empleado"`) todavía puede no tener el módulo de `ruta` en `perms.tickets`, y el
 * negocio puede tener apagada la tarjeta de `ruta`: en ambos casos se lo manda a la primera sección
 * que sí puede ver, no a la que pidió.
 */
export const RequireOrganizerRole = ({ children, ruta }) => {
  const auth = useSelector((state) => state.auth);
  const role = auth?.user?.role;
  if (role !== "ORGANIZER") return <Navigate to="/organizer/presencial" replace />;
  if (!puedeVerRuta(auth, ruta) || !negocioMuestraRuta(auth?.negocio, ruta)) return aSeccionPermitida(auth, ruta);
  return children;
};

/**
 * Solo la tarjeta del negocio, para las rutas que no tenían guarda de `perms` (presencial, gifts).
 * SELLER queda afuera: su panel es acotado y no depende de las tarjetas.
 */
export const RequireTarjetaDelNegocio = ({ children, ruta }) => {
  const auth = useSelector((state) => state.auth);
  if (auth?.user?.role === "SELLER") return children;
  if (!negocioMuestraRuta(auth?.negocio, ruta)) return aSeccionPermitida(auth, ruta);
  return children;
};

/** El índice de /organizer: el dashboard agrega datos dueño-solo, así que un empleado no entra. */
export const OrganizerIndexGate = () => {
  const auth = useSelector((state) => state.auth);
  const role = auth?.user?.role;
  if (role === "SELLER") return <Navigate to="/organizer/presencial" replace />;
  if (!puedeVerRuta(auth, "/organizer") || !negocioMuestraRuta(auth?.negocio, "/organizer")) {
    return aSeccionPermitida(auth, "/organizer");
  }
  return <OrganizerDashboard />;
};
